import React, { useEffect, useMemo, useState } from "react";
import CommonFooter from "../../../core/common/footer/commonFooter";
import { DatePicker, Table, message } from "antd";
import TooltipIcons from "../../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../../core/common/tooltip-content/collapes";
import { all_routes } from "../../../Router/all_routes";
import { Link } from "react-router-dom";
import Select from "react-select";
import {
  getProductReportFilters,
  getProductReportList,
  getProductReportSummary,
  downloadProductReportExcel,
  downloadProductReportPdf,
} from "../../../api/productReportApi";

const DEFAULT_PRODUCT_IMAGE = "/assets/img/by-default.png";
const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const isAbsoluteHttp = (url = "") => /^https?:\/\//i.test(url);
const isDataUrl = (url = "") => /^data:image/i.test(url);

const resolveImageUrl = (raw) => {
  if (!raw) return DEFAULT_PRODUCT_IMAGE;
  const baseVal = Array.isArray(raw) ? raw[0] : raw;
  const val =
    typeof baseVal === "object" && baseVal !== null
      ? baseVal.url ||
        baseVal.imageUrl ||
        baseVal.key ||
        baseVal.path ||
        baseVal.location ||
        baseVal.src ||
        ""
      : baseVal;

  if (!val || typeof val !== "string") return DEFAULT_PRODUCT_IMAGE;
  if (isDataUrl(val) || isAbsoluteHttp(val)) return val;

  const key = val.replace(/^\/+/, "");
  if (!key) return DEFAULT_PRODUCT_IMAGE;

  return `${IMAGE_BASE}/${key}`;
};

const cardBaseStyle = {
  border: "0",
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(17, 24, 39, 0.08)",
};

const actionBtnBase = {
  minWidth: "132px",
  height: "42px",
  borderRadius: "10px",
  fontWeight: 600,
  fontSize: "14px",
};

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    height: 46,
    borderRadius: 10,
    borderColor: state.isFocused ? "#f7971e" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(247,151,30,0.15)" : "none",
    "&:hover": {
      borderColor: "#f7971e",
    },
    cursor: "pointer",
    backgroundColor: "#fff",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 12px",
    flexWrap: "nowrap",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#212529",
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#adb5bd",
    fontSize: 14,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: 14,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 46,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#f7971e" : "#adb5bd",
    "&:hover": { color: "#f7971e" },
    padding: "0 10px",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#adb5bd",
    "&:hover": { color: "#dc3545" },
    padding: "0 6px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 10,
    boxShadow: "0 8px 30px rgba(17,24,39,0.15)",
    border: "1px solid #eef1f4",
    overflow: "hidden",
    marginTop: 4,
  }),
  menuList: (base) => ({
    ...base,
    padding: "6px",
    maxHeight: 240,
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 7,
    fontSize: 14,
    padding: "9px 12px",
    backgroundColor: state.isSelected
      ? "#f7971e"
      : state.isFocused
      ? "#fff8ee"
      : "transparent",
    color: state.isSelected ? "#fff" : "#212529",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#f7971e",
      color: "#fff",
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: "#adb5bd",
  }),
  loadingMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: "#adb5bd",
  }),
};

const Productreport = () => {
  const route = all_routes;
  const t = window.translate;

  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalQty: 0,
    totalOrdered: 0,
    totalRevenue: 0,
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
  });

  const [filterOptions, setFilterOptions] = useState({
    stores: [],
    categories: [],
    brands: [],
    products: [],
  });

  const [filters, setFilters] = useState({
    date: null,
    store: null,
    category: null,
    brand: null,
    product: null,
  });

  const lang = localStorage.getItem("lang") || "en";

  const storeOptions = useMemo(() => {
    return (filterOptions.stores || []).map((item) => ({
      value: item?._id,
      label:
        item?.storeName?.[lang] ||
        item?.storeName?.en ||
        item?.storeName?.ar ||
        "N/A",
    }));
  }, [filterOptions.stores, lang]);

  const categoryOptions = useMemo(() => {
    return (filterOptions.categories || []).map((item) => ({
      value: item?._id,
      label: item?.name?.[lang] || item?.name?.en || item?.name?.ar || "N/A",
    }));
  }, [filterOptions.categories, lang]);

  const brandOptions = useMemo(() => {
    return (filterOptions.brands || []).map((item) => ({
      value: item?._id,
      label:
        item?.brandName?.[lang] ||
        item?.brandName?.en ||
        item?.brandName?.ar ||
        "N/A",
    }));
  }, [filterOptions.brands, lang]);

  const productOptions = useMemo(() => {
    return (filterOptions.products || []).map((item) => ({
      value: item?._id,
      label:
        item?.productName?.[lang] ||
        item?.productName?.en ||
        item?.productName?.ar ||
        item?.sku ||
        "N/A",
    }));
  }, [filterOptions.products, lang]);

  const buildParams = (customFilters = filters, extra = {}) => ({
    page: extra.page || 1,
    limit: extra.limit || 100,
    date: customFilters?.date ? customFilters.date.format("YYYY-MM-DD") : "",
    store: customFilters?.store?.value || "",
    category: customFilters?.category?.value || "",
    brand: customFilters?.brand?.value || "",
    product: customFilters?.product?.value || "",
  });

  const fetchFilters = async () => {
    try {
      setFilterLoading(true);
      const response = await getProductReportFilters();
      setFilterOptions(response?.data || {});
    } catch (error) {
      message.error(error?.message || t("Failed to load product report filters"));
    } finally {
      setFilterLoading(false);
    }
  };

  const fetchProductReport = async (customFilters = filters) => {
    try {
      setLoading(true);

      const params = buildParams(customFilters, { page: 1, limit: 100 });

      const [reportResponse, summaryResponse] = await Promise.all([
        getProductReportList(params),
        getProductReportSummary(params),
      ]);

      const rows = Array.isArray(reportResponse?.data) ? reportResponse.data : [];

      const formattedData = rows.map((item, index) => ({
        key: item?._id || `${item?.productId || "row"}-${index}`,
        SKU: item?.sku || "N/A",
        Product_Name: item?.productName || "N/A",
        Category: item?.category || "N/A",
        Brand: item?.brand || "N/D",
        Qty: Number(item?.qty || 0),
        Price: Number(item?.price || 0),
        Total_Ordered: Number(item?.totalOrdered || 0),
        Revenue: Number(item?.revenue || 0),
        image: resolveImageUrl(item?.image || item?.images || item?.productImage),
        raw: item,
      }));

      setTableData(formattedData);

      setPagination(
        reportResponse?.pagination || {
          total: formattedData.length,
          page: 1,
          limit: 100,
          totalPages: 1,
        }
      );

      setSummary(
        summaryResponse?.data ||
          reportResponse?.summary || {
            totalRows: 0,
            totalQty: 0,
            totalOrdered: 0,
            totalRevenue: 0,
          }
      );
    } catch (error) {
      console.error("Product report fetch error:", error);
      message.error(error?.message || t("Failed to fetch product report"));
      setTableData([]);
      setSummary({
        totalRows: 0,
        totalQty: 0,
        totalOrdered: 0,
        totalRevenue: 0,
      });
      setPagination({ total: 0, page: 1, limit: 50, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchProductReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    await fetchProductReport(filters);
  };

  const handleRefresh = async (e) => {
    e.preventDefault();
    await fetchFilters();
    await fetchProductReport(filters);
  };

  const handleDownloadExcel = async () => {
    try {
      setExportLoading(true);
      await downloadProductReportExcel(buildParams(filters, { page: 1, limit: 1000 }));
      message.success(t("Product report excel downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to download product report excel"));
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setExportLoading(true);
      await downloadProductReportPdf(buildParams(filters, { page: 1, limit: 1000 }));
      message.success(t("Product report pdf downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to download product report pdf"));
    } finally {
      setExportLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const number = Number(value || 0);
    return `$${number.toLocaleString()}`;
  };

  const columns = [
    {
      title: t("SKU"),
      dataIndex: "SKU",
      render: (text) => (
        <Link to="#" className="text-orange fw-medium">
          {text}
        </Link>
      ),
      sorter: (a, b) => (a?.SKU || "").localeCompare(b?.SKU || ""),
    },
    {
      title: t("Product Name"),
      dataIndex: "Product_Name",
      render: (text, record) => (
        <div className="d-flex align-items-center" style={{ minWidth: 220 }}>
          <div
            className="me-2 d-flex align-items-center justify-content-center"
            style={{
              width: 42,
              height: 42,
              minWidth: 42,
              borderRadius: 10,
              background: "#f8f9fa",
              overflow: "hidden",
              border: "1px solid #eef1f4",
            }}
          >
            <img
              src={record?.image || DEFAULT_PRODUCT_IMAGE}
              className="img-fluid"
              alt="img"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
              }}
            />
          </div>
          <div>
            <p
              className="text-dark mb-0 fw-medium"
              style={{ lineHeight: "1.35", wordBreak: "break-word" }}
            >
              <Link to="#" className="text-dark">
                {text || "N/A"}
              </Link>
            </p>
          </div>
        </div>
      ),
      sorter: (a, b) =>
        (a?.Product_Name || "").localeCompare(b?.Product_Name || ""),
    },
    {
      title: t("Category"),
      dataIndex: "Category",
      sorter: (a, b) => (a?.Category || "").localeCompare(b?.Category || ""),
    },
    {
      title: t("Brand"),
      dataIndex: "Brand",
      sorter: (a, b) => (a?.Brand || "").localeCompare(b?.Brand || ""),
    },
    {
      title: t("Qty"),
      dataIndex: "Qty",
      render: (text) => Number(text || 0),
      sorter: (a, b) => Number(a?.Qty || 0) - Number(b?.Qty || 0),
    },
    {
      title: t("Price"),
      dataIndex: "Price",
      render: (text) => formatCurrency(text || 0),
      sorter: (a, b) => Number(a?.Price || 0) - Number(b?.Price || 0),
    },
    {
      title: t("Total Ordered"),
      dataIndex: "Total_Ordered",
      render: (text) => Number(text || 0),
      sorter: (a, b) =>
        Number(a?.Total_Ordered || 0) - Number(b?.Total_Ordered || 0),
    },
    {
      title: t("Revenue"),
      dataIndex: "Revenue",
      render: (text) => (
        <span className="fw-semibold">{formatCurrency(text || 0)}</span>
      ),
      sorter: (a, b) => Number(a?.Revenue || 0) - Number(b?.Revenue || 0),
    },
  ];

  const statCards = [
    {
      title: t("Total Rows"),
      value: summary?.totalRows || tableData.length || 0,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      icon: "ti ti-layout-grid",
    },
    {
      title: t("Total Qty"),
      value: summary?.totalQty || 0,
      gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
      icon: "ti ti-package",
    },
    {
      title: t("Total Ordered"),
      value: summary?.totalOrdered || 0,
      gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
      icon: "ti ti-shopping-cart",
    },
    {
      title: t("Revenue"),
      value: formatCurrency(summary?.totalRevenue || 0),
      gradient: "linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)",
      icon: "ti ti-cash-banknote",
    },
  ];

  return (
    <div>
      <style>
        {`
          .product-report-datepicker {
            width: 100% !important;
            height: 46px !important;
            border-radius: 10px !important;
            display: flex !important;
            align-items: center !important;
            padding: 0 12px !important;
            border-color: #dee2e6 !important;
            box-shadow: none !important;
            background: #fff !important;
          }

          .product-report-datepicker:hover,
          .product-report-datepicker.ant-picker-focused {
            border-color: #f7971e !important;
            box-shadow: 0 0 0 2px rgba(247,151,30,0.15) !important;
          }

          .product-report-datepicker .ant-picker-input {
            height: 100% !important;
            display: flex !important;
            align-items: center !important;
          }

          .product-report-datepicker .ant-picker-input > input {
            font-size: 14px !important;
            color: #212529 !important;
            height: 100% !important;
          }

          .product-report-datepicker .ant-picker-input > input::placeholder {
            color: #adb5bd !important;
          }

          .product-report-datepicker .ant-picker-suffix {
            color: #adb5bd !important;
          }

          .ant-picker-dropdown {
            z-index: 9999 !important;
          }
        `}
      </style>

      <div className="page-wrapper">
        <div className="content" style={{ paddingBottom: 24 }}>
          <div className="table-tab mb-4">
            <ul className="nav nav-pills" style={{ gap: 10, flexWrap: "wrap" }}>
              <li className="nav-item">
                <Link className="nav-link active" to={route.productreport}>
                  {t("Product Report")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={route.productexpiredreport}>
                  {t("Product Expiry Report")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={route.productquantityreport}>
                  {t("Product Quantity Alert")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="page-header mb-4">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="mb-1">{t("Product Report")}</h4>
                <h6 className="mb-0">{t("View Reports of Products")}</h6>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn"
                onClick={handleDownloadExcel}
                disabled={exportLoading}
                style={{
                  ...actionBtnBase,
                  border: "1px solid #0d6efd",
                  color: "#0d6efd",
                  background: "#fff",
                }}
              >
                {exportLoading ? t("Please wait...") : t("Export Excel")}
              </button>

              <button
                type="button"
                className="btn"
                onClick={handleDownloadPdf}
                disabled={exportLoading}
                style={{
                  ...actionBtnBase,
                  border: "1px solid #198754",
                  color: "#198754",
                  background: "#fff",
                }}
              >
                {exportLoading ? t("Please wait...") : t("Export PDF")}
              </button>

              <ul className="table-top-head" style={{ marginBottom: 0 }}>
                <li>
                  <Link to="#" onClick={handleRefresh}>
                    <RefreshIcon />
                  </Link>
                </li>
                <CollapesIcon />
              </ul>
            </div>
          </div>

          <div
            className="card shadow-sm border-0 mb-4"
            style={{
              ...cardBaseStyle,
              overflow: "visible",
            }}
          >
            <div className="card-body" style={{ padding: "22px 22px 8px" }}>
              <form onSubmit={handleGenerateReport}>
                <div className="row g-3 align-items-end">
                  <div className="col-xxl-2 col-xl-4 col-md-6">
                    <label className="form-label fw-medium mb-2">
                      {t("Choose Date")}
                    </label>

                    <DatePicker
                      className="product-report-datepicker"
                      placeholder={t("dd/mm/yyyy")}
                      value={filters.date}
                      onChange={(date) =>
                        setFilters((prev) => ({ ...prev, date }))
                      }
                      format="DD/MM/YYYY"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                  </div>
                  

                  <div className="col-xxl-2 col-xl-4 col-md-6 me-4">
                    <label className="form-label fw-medium mb-2">{t("Store")}</label>
                    <Select
                      classNamePrefix="react-select"
                      options={storeOptions}
                      placeholder={t("Choose")}
                      value={filters.store}
                      onChange={(selected) =>
                        setFilters((prev) => ({ ...prev, store: selected }))
                      }
                      isClearable
                      isLoading={filterLoading}
                      styles={reactSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="col-xxl-2 col-xl-4 col-md-6">
                    <label className="form-label fw-medium mb-2">
                      {t("Category")}
                    </label>
                    <Select
                      classNamePrefix="react-select"
                      options={categoryOptions}
                      placeholder={t("Choose")}
                      value={filters.category}
                      onChange={(selected) =>
                        setFilters((prev) => ({ ...prev, category: selected }))
                      }
                      isClearable
                      isLoading={filterLoading}
                      styles={reactSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="col-xxl-2 col-xl-4 col-md-6">
                    <label className="form-label fw-medium mb-2">{t("Brand")}</label>
                    <Select
                      classNamePrefix="react-select"
                      options={brandOptions}
                      placeholder={t("Choose")}
                      value={filters.brand}
                      onChange={(selected) =>
                        setFilters((prev) => ({ ...prev, brand: selected }))
                      }
                      isClearable
                      isLoading={filterLoading}
                      styles={reactSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="col-xxl-2 col-xl-4 col-md-6">
                    <label className="form-label fw-medium mb-2">
                      {t("Product")}
                    </label>
                    <Select
                      classNamePrefix="react-select"
                      options={productOptions}
                      placeholder={t("Choose")}
                      value={filters.product}
                      onChange={(selected) =>
                        setFilters((prev) => ({ ...prev, product: selected }))
                      }
                      isClearable
                      isLoading={filterLoading}
                      styles={reactSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="col-xxl-2 col-xl-4 col-md-6">
                    <button
                      className="btn w-100"
                      type="submit"
                      disabled={loading}
                      style={{
                        height: 46,
                        borderRadius: 10,
                        background:
                          "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
                        border: "none",
                        color: "#fff",
                        fontWeight: 700,
                        boxShadow: "0 8px 18px rgba(247,151,30,0.25)",
                      }}
                    >
                      {loading ? t("Please wait...") : t("Generate Report")}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="row g-3 mb-4">
            {statCards.map((card, index) => (
              <div
                className="col-xxl-3 col-xl-6 col-md-6 col-12 d-flex"
                key={index}
              >
                <div
                  className="card w-100 border-0"
                  style={{
                    ...cardBaseStyle,
                    background: card.gradient,
                    minHeight: 112,
                  }}
                >
                  <div
                    className="card-body d-flex justify-content-between align-items-center"
                    style={{ padding: "22px 20px" }}
                  >
                    <div>
                      <h6
                        style={{
                          color: "rgba(255,255,255,0.92)",
                          marginBottom: 10,
                          fontSize: 15,
                          fontWeight: 500,
                        }}
                      >
                        {card.title}
                      </h6>
                      <h2
                        style={{
                          color: "#fff",
                          margin: 0,
                          fontWeight: 700,
                          fontSize: 24,
                          lineHeight: 1.1,
                        }}
                      >
                        {card.value}
                      </h2>
                    </div>
                    <div
                      style={{
                        width: 62,
                        height: 62,
                        borderRadius: 16,
                        background: "rgba(255,255,255,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 28,
                        flexShrink: 0,
                      }}
                    >
                      <i className={card.icon} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="card table-list-card no-search border-0 shadow-sm"
            style={cardBaseStyle}
          >
            <div
              className="card-header d-flex align-items-center justify-content-between flex-wrap"
              style={{
                padding: "22px 22px 16px",
                background: "#fff",
                borderBottom: "1px solid #eef1f4",
                gap: 12,
              }}
            >
              <div>
                <h4 className="mb-1">{t("Product Report")}</h4>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  {t("Clean view of product inventory and sales summary")}
                </p>
              </div>

              <ul className="table-top-head" style={{ marginBottom: 0 }}>
                <TooltipIcons />
                <li>
                  <Link to="#" onClick={handleDownloadPdf}>
                    <i className="ti ti-printer" />
                  </Link>
                </li>
              </ul>
            </div>

            <div className="card-body" style={{ padding: "18px 22px 22px" }}>
              <div
                className="d-flex justify-content-between align-items-center flex-wrap"
                style={{
                  marginBottom: 14,
                  gap: 10,
                  paddingBottom: 10,
                  borderBottom: "1px solid #f1f3f5",
                }}
              >
                <div className="text-muted" style={{ fontSize: 15 }}>
                  {t("API Records")}:{" "}
                  <strong>{Array.isArray(tableData) ? tableData.length : 0}</strong>
                </div>
                <div className="text-muted" style={{ fontSize: 15 }}>
                  {t("Total")}: <strong>{pagination?.total || 0}</strong>
                </div>
              </div>

              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={Array.isArray(tableData) ? tableData : []}
                  loading={loading}
                  pagination={false}
                />
              </div>

              <div
                className="d-flex justify-content-between align-items-center mt-3 flex-wrap"
                style={{
                  gap: 10,
                  paddingTop: 12,
                  borderTop: "1px solid #f1f3f5",
                }}
              >
                <div className="text-dark" style={{ fontSize: 15 }}>
                  <strong>{t("Total")}:</strong> {pagination?.total || 0}
                </div>
                <div className="text-dark" style={{ fontSize: 15 }}>
                  <strong>{t("Page")}:</strong> {pagination?.page || 1} /{" "}
                  {pagination?.totalPages || 1}
                </div>
              </div>
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>
    </div>
  );
};

export default Productreport;